import { AxiosResponse } from 'axios';
import Entity from './shared/entity';
import Fields, { MappedEventField } from './shared/fields';
import Image from './shared/image';
import ServiceClient from './shared/service-client/ServiceClient';

export interface MessageResponse {
  message: string;
}

export enum TransferType {
  GIFT = 'GIFT',
  STANDARD = 'STANDARD',
}
export interface Transfer {
  identityId: string;
  type: TransferType;
}

export interface DigitalTwin {
  id: string;
  hasOwner: boolean;
  serialNumber?: string;
  owner?: string;
  lost?: boolean;
  transfer?: Transfer;
  specification?: Specification;
  verified?: boolean;
  custodian?: string;
  warranty?: ActiveWarranty | null;
  nfcTag?: NfcTag;
}

export interface Certificate extends CertificateProperties, Entity {}

interface CertificateProperties {
  name: string;
  icon: Image;
  image?: Image | null;
  description?: string | null;
  website?: string | null;
}

export interface ProvenanceMap extends ProvenanceMapProperties, Entity {}
interface ProvenanceMapProperties {
  events: {
    [eventType in EventTypes]?: MappedEvent;
  };
  name: string;
}

export interface MappedEvent {
  hideEvent?: boolean;
  provenanceEvents: MappedEventField[];
}

export interface Specification {
  presentationId: string;
  sku: string;
  name: string;
  image?: Image | null;
  shortDescription?: string | null;
  upc?: number | null;
  fields: Fields;
  certificates: Certificate[];
  provenanceMapId?: string;
}

interface Warranty {
  title: string;
  description: string;
  duration: number;
}

interface NfcTag {
  uid: string;
  locked: boolean;
  type: string;
}

interface ActiveWarranty extends Warranty {
  expiresAt: number;
}

interface Custodian {
  id: string;
  name: string;
  location: string;
}

interface Event {
  twinId: string;
  occurredAt: number;
  registeredAt: number;
  identity: string;
  custodian?: Custodian;
}

export enum EventTypes {
  SOLD = 'SOLD',
  OWNERSHIP_REVOKED = 'OWNERSHIP_REVOKED',
  TRANSFER_ACCEPTED = 'TRANSFER_ACCEPTED',
  CREATED = 'CREATED',
  SPECIFICATION_UPDATED = 'SPECIFICATION_UPDATED',
  TRANSFER_CANCELED = 'TRANSFER_CANCELED',
  CLAIMED_WITH_VOUCHER = 'CLAIMED_WITH_VOUCHER',
  OWNERSHIP_REQUEST_ACCEPTED = 'OWNERSHIP_REQUEST_ACCEPTED',
  TRANSFER_DENIED = 'TRANSFER_DENIED',
  TRANSFER_INITIATED = 'TRANSFER_INITIATED',
  CUSTODIAN_UPDATED = 'CUSTODIAN_UPDATED',
  WARRANTY_UPDATED = 'WARRANTY_UPDATED',
  NFCTAG_UPDATED = 'NFCTAG_UPDATED',
  UPDATED_LOST = 'UPDATED_LOST',
  VERIFIED = 'VERIFIED',
}

export interface SoldEvent extends Event {
  type: typeof EventTypes.SOLD;
  data: { newOwner: string; price?: number };
}

interface OwnershipRevokedEvent extends Event {
  type: typeof EventTypes.OWNERSHIP_REVOKED;
}

interface TransferAcceptedEvent extends Event {
  type: typeof EventTypes.TRANSFER_ACCEPTED;
}

interface CreatedEvent extends Event {
  type: typeof EventTypes.CREATED;
  data: { serialNumber?: string; specification?: Specification };
}

interface SpecificationUpdatedEvent extends Event {
  type: typeof EventTypes.SPECIFICATION_UPDATED;
  data: { specification: Specification | null | undefined };
}

interface TransferCanceledEvent extends Event {
  type: typeof EventTypes.TRANSFER_CANCELED;
}

interface ClaimedWithVoucherEvent extends Event {
  type: typeof EventTypes.CLAIMED_WITH_VOUCHER;
}

export interface OwnershipRequestAcceptedEvent extends Event {
  type: typeof EventTypes.OWNERSHIP_REQUEST_ACCEPTED;
  data: {
    newOwner: string;
    reseller?: Custodian | null;
    dateOfPurchase?: number | null;
    responderCustodian: Custodian;
    responseComment?: string;
  };
}

interface TransferDeniedEvent extends Event {
  type: typeof EventTypes.TRANSFER_DENIED;
}

interface TransferInitiatedEvent extends Event {
  type: typeof EventTypes.TRANSFER_INITIATED;
  data: { transfer: Transfer };
}

interface CustodianUpdatedEvent extends Event {
  type: typeof EventTypes.CUSTODIAN_UPDATED;
  data: { newCustodian: string };
}

interface WarrantyUpdatedEvent extends Event {
  type: typeof EventTypes.WARRANTY_UPDATED;
  data: { warranty: Warranty };
}

interface NfcTagUpdatedEvent extends Event {
  type: typeof EventTypes.NFCTAG_UPDATED;
  data: { nfcTag: NfcTag };
}

interface UpdatedLostEvent extends Event {
  type: typeof EventTypes.UPDATED_LOST;
  data: { lost: boolean };
}

interface VerifiedEvent extends Event {
  type: typeof EventTypes.VERIFIED;
}

export type DigitalTwinEvent =
  | SoldEvent
  | OwnershipRevokedEvent
  | TransferAcceptedEvent
  | CreatedEvent
  | SpecificationUpdatedEvent
  | TransferCanceledEvent
  | ClaimedWithVoucherEvent
  | TransferDeniedEvent
  | TransferInitiatedEvent
  | CustodianUpdatedEvent
  | UpdatedLostEvent
  | VerifiedEvent
  | WarrantyUpdatedEvent
  | NfcTagUpdatedEvent
  | OwnershipRequestAcceptedEvent;

export type PERMISSIONS =
  | typeof SELL
  | typeof REVOKE_OWNERSHIP
  | typeof CREATE
  | typeof SET_CUSTODIAN
  | typeof SET_WARRANTY
  | typeof SET_NFCTAG
  | typeof UPDATE_SPECIFICATION
  | typeof VERIFY
  | typeof GET
  | typeof GET_ALL
  | typeof GET_ALL_ANY
  | typeof GET_ALL_SAME_CUSTODIAN
  | typeof GET_EVENTS
  | typeof GET_EVENTS_ANY
  | typeof GET_EVENTS_VERIFIED
  | typeof GET_EVENTS_SOLD
  | typeof GET_EVENTS_CREATED
  | typeof GET_EVENTS_SPECIFICATION_UPDATED
  | typeof GET_EVENTS_TRANSFER_INITIATED
  | typeof GET_EVENTS_TRANSFER_DENIED
  | typeof GET_EVENTS_TRANSFER_CANCELED
  | typeof GET_EVENTS_TRANSFER_ACCEPTED
  | typeof GET_EVENTS_CUSTODIAN_UPDATED
  | typeof GET_EVENTS_CLAIMED_WITH_VOUCHER
  | typeof GET_EVENTS_UPDATED_LOST
  | typeof GET_PROVENANCE
  | typeof OWNERSHIP_REQUEST_RESPONSE
  | typeof OWNERSHIP_REQUEST_GET_ALL
  | typeof CERTIFICATE_GET_ALL
  | typeof CERTIFICATE_CREATE
  | typeof CERTIFICATE_UPDATE
  | typeof CERTIFICATE_DELETE
  | typeof PROVENANCE_MAP_GET_ALL
  | typeof PROVENANCE_MAP_CREATE
  | typeof PROVENANCE_MAP_UPDATE
  | typeof PROVENANCE_MAP_DELETE;

export const CREATE = 'DIGITALTWIN:CREATE';
export const SELL = 'DIGITALTWIN:SELL';
export const REVOKE_OWNERSHIP = 'DIGITALTWIN:REVOKE_OWNERSHIP';
export const SET_CUSTODIAN = 'DIGITALTWIN:SET_CUSTODIAN';
export const SET_WARRANTY = 'DIGITALTWIN:SET_WARRANTY';
export const SET_NFCTAG = 'DIGITALTWIN:SET_NFCTAG';
export const UPDATE_SPECIFICATION = 'DIGITALTWIN:UPDATE_SPECIFICATION';
export const VERIFY = 'DIGITALTWIN:VERIFY';

export const GET = 'DIGITALTWIN:GET';
export const GET_ALL = 'DIGITALTWIN:GET_ALL';
export const GET_ALL_ANY = 'DIGITALTWIN:GET_ALL:ANY';
export const GET_ALL_SAME_CUSTODIAN = 'DIGITALTWIN:GET_ALL:SAME_CUSTODIAN';

export const GET_PROVENANCE = 'DIGITALTWIN:GET_PROVENANCE';

export const GET_EVENTS = 'DIGITALTWIN:GET_EVENTS';
export const GET_EVENTS_ANY = 'DIGITALTWIN:GET_EVENTS:ANY';
export const GET_EVENTS_VERIFIED = 'DIGITALTWIN:GET_EVENTS:VERIFIED';
export const GET_EVENTS_SOLD = 'DIGITALTWIN:GET_EVENTS:SOLD';
export const GET_EVENTS_OWNERSHIP_REVOKED = 'DIGITALTWIN:GET_EVENTS:OWNERSHIP_REVOKED';
export const GET_EVENTS_CREATED = 'DIGITALTWIN:GET_EVENTS:CREATED';
export const GET_EVENTS_SPECIFICATION_UPDATED = 'DIGITALTWIN:GET_EVENTS:SPECIFICATION_UPDATED';
export const GET_EVENTS_TRANSFER_INITIATED = 'DIGITALTWIN:GET_EVENTS:TRANSFER_INITIATED';
export const GET_EVENTS_TRANSFER_DENIED = 'DIGITALTWIN:GET_EVENTS:TRANSFER_DENIED';
export const GET_EVENTS_TRANSFER_CANCELED = 'DIGITALTWIN:GET_EVENTS:TRANSFER_CANCELED';
export const GET_EVENTS_TRANSFER_ACCEPTED = 'DIGITALTWIN:GET_EVENTS:TRANSFER_ACCEPTED';
export const GET_EVENTS_CUSTODIAN_UPDATED = 'DIGITALTWIN:GET_EVENTS:CUSTODIAN_UPDATED';
export const GET_EVENTS_CLAIMED_WITH_VOUCHER = 'DIGITALTWIN:GET_EVENTS:CLAIMED_WITH_VOUCHER';
export const GET_EVENTS_UPDATED_LOST = 'DIGITALTWIN:GET_EVENTS:UPDATED_LOST';

export const CERTIFICATE_CREATE = 'DIGITALTWIN:CERTIFICATE:CREATE';
export const CERTIFICATE_GET_ALL = 'DIGITALTWIN:CERTIFICATE:GET_ALL';
export const CERTIFICATE_UPDATE = 'DIGITALTWIN:CERTIFICATE:UPDATE';
export const CERTIFICATE_DELETE = 'DIGITALTWIN:CERTIFICATE:DELETE';

export const PROVENANCE_MAP_CREATE = 'DIGITALTWIN:PROVENANCE_MAP:CREATE';
export const PROVENANCE_MAP_UPDATE = 'DIGITALTWIN:PROVENANCE_MAP:UPDATE';
export const PROVENANCE_MAP_GET_ALL = 'DIGITALTWIN:PROVENANCE_MAP:GET_ALL';
export const PROVENANCE_MAP_DELETE = 'DIGITALTWIN:PROVENANCE_MAP:DELETE';

export const OWNERSHIP_REQUEST_RESPONSE = 'DIGITALTWIN:OWNERSHIP_REQUEST:RESPONSE';
export const OWNERSHIP_REQUEST_GET_ALL = 'DIGITALTWIN:OWNERSHIP_REQUEST:GET_ALL';

export const PERMISSION_NAMES = {
  CREATE,
  SELL,
  REVOKE_OWNERSHIP,
  SET_CUSTODIAN,
  SET_WARRANTY,
  SET_NFCTAG,
  UPDATE_SPECIFICATION,
  VERIFY,
  GET,
  GET_ALL,
  GET_ALL_ANY,
  GET_ALL_SAME_CUSTODIAN,
  GET_EVENTS,
  GET_EVENTS_ANY,
  GET_EVENTS_VERIFIED,
  GET_EVENTS_SOLD,
  GET_EVENTS_OWNERSHIP_REVOKED,
  GET_EVENTS_CREATED,
  GET_EVENTS_SPECIFICATION_UPDATED,
  GET_EVENTS_TRANSFER_INITIATED,
  GET_EVENTS_TRANSFER_DENIED,
  GET_EVENTS_TRANSFER_CANCELED,
  GET_EVENTS_TRANSFER_ACCEPTED,
  GET_EVENTS_CUSTODIAN_UPDATED,
  GET_EVENTS_CLAIMED_WITH_VOUCHER,
  GET_EVENTS_UPDATED_LOST,
  GET_PROVENANCE,
  CERTIFICATE_CREATE,
  CERTIFICATE_GET_ALL,
  CERTIFICATE_UPDATE,
  CERTIFICATE_DELETE,
  OWNERSHIP_REQUEST_RESPONSE,
  OWNERSHIP_REQUEST_GET_ALL,
  PROVENANCE_MAP_CREATE,
  PROVENANCE_MAP_UPDATE,
  PROVENANCE_MAP_GET_ALL,
  PROVENANCE_MAP_DELETE,
};

export interface DateRange {
  start: number;
  end: number;
}

export interface GetAllOptions {
  dateRange?: DateRange;
  owner?: string;
  serialNumber?: string;
}

export interface GetAllCertificatesOptions {
  dateRange?: DateRange;
}

export interface GetAllProvenanceMapsOptions {
  dateRange?: DateRange;
}

export interface EventsQueryOptions {
  dateRange: DateRange;
  event?: string | string[];
}

export interface DigitaltwinsResponse {
  digitaltwins: DigitalTwin[];
}

export interface CertificatesResponse {
  certificates: Certificate[];
}

export interface ProvenanceMapsResponse {
  provenanceMaps: ProvenanceMap[];
}

export interface OwnershipRequestsResponse {
  ownershipRequests: OwnershipRequest[];
  continuationToken?: string;
}

export interface DigitalTwinEventsResponse {
  events: DigitalTwinEvent[];
  map?: ProvenanceMap;
}

export type CreateDigitaltwin = Pick<DigitalTwin, 'serialNumber'> & { specification?: CreateSpecification } & {
  occurredAt?: number;
};
export type CreateSpecification = Omit<Specification, 'certificates'> & { certificates?: string[] };
export type UpdateSpecification = Partial<CreateSpecification>;

export type CreateCertificate = CertificateProperties;
export type UpdateCertificate = Partial<CertificateProperties>;
export interface DeleteCertificateResponse {
  deleted: boolean;
  id: string;
}

export type CreateProvenanceMap = ProvenanceMapProperties;
export type UpdateProvenanceMap = Partial<ProvenanceMapProperties>;
export interface DeleteProvenanceMapResponse {
  deleted: boolean;
  id: string;
}

export enum OWNERSHIP_REQUEST_STATUS {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}
export interface OwnershipRequestProperties {
  twinId: string;
  comment?: string;
  images: Image[];
  status: OWNERSHIP_REQUEST_STATUS;
  responseComment?: string;
  reseller?: Custodian;
  dateOfPurchase?: number;
}

export interface GetAllOwnershipRequests {
  limit?: number;
  continuationToken?: string;
  status?: OWNERSHIP_REQUEST_STATUS;
}

export interface AcceptOwnershipRequest {
  responseComment: string;
  status: OWNERSHIP_REQUEST_STATUS.ACCEPTED;
  dateOfPurchase?: number;
  reseller?: Custodian;
}

export interface RejectOwnershipRequest {
  responseComment: string;
  status: OWNERSHIP_REQUEST_STATUS.REJECTED;
}

export interface PendingOwnershipRequest {
  responseComment: string;
  status: OWNERSHIP_REQUEST_STATUS.PENDING;
}

export interface OwnershipRequest extends OwnershipRequestProperties, Entity {}

export class DigitaltwinClient extends ServiceClient {
  public async getAll(options: GetAllOptions = {}): Promise<AxiosResponse<DigitaltwinsResponse>> {
    const { owner, dateRange, serialNumber } = options;
    const url = '/';
    return await this.httpClient.get(url, {
      params: {
        owner,
        start: dateRange?.start,
        end: dateRange?.end,
        serialNumber,
      },
      ...this.requestConfig,
    });
  }

  public async events(options: EventsQueryOptions): Promise<AxiosResponse<DigitalTwinEventsResponse>> {
    const { dateRange } = options;
    let { event } = options;
    const url = '/events';

    if (Array.isArray(event)) {
      event = JSON.stringify(event);
    }

    return await this.httpClient.get(url, {
      params: {
        start: dateRange?.start,
        end: dateRange?.end,
        event,
      },
      ...this.requestConfig,
    });
  }

  public async get(twinId: string): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.get(`/${twinId}`, this.requestConfig);
  }

  public async getProvenance(twinId: string): Promise<AxiosResponse<DigitalTwinEventsResponse>> {
    return await this.httpClient.get(`/${twinId}/provenance`, this.requestConfig);
  }

  public async getByOwner(ownerId: string): Promise<AxiosResponse<DigitaltwinsResponse>> {
    return await this.getAll({ owner: ownerId });
  }

  public async updateLost(twinId: string, lost: boolean): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.put(`/${twinId}/lost`, { lost }, this.requestConfig);
  }

  public async create(create: CreateDigitaltwin): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.post('/', create, this.requestConfig);
  }

  public async createBatch(digitaltwins: CreateDigitaltwin[]): Promise<AxiosResponse<DigitaltwinsResponse>> {
    return await this.httpClient.post('/batch', { digitaltwins }, this.requestConfig);
  }

  public async updateSpecification(
    twinId: string,
    specification?: UpdateSpecification | null,
    occurredAt?: number,
  ): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.patch(`/${twinId}/specification`, { specification, occurredAt }, this.requestConfig);
  }

  public async sell(
    twinId: string,
    newOwner: string,
    occurredAt?: number,
    price?: number,
  ): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.post(
      `/${twinId}/sell`,
      {
        newOwner,
        price,
        occurredAt,
      },
      this.requestConfig,
    );
  }

  public async revokeOwnership(twinId: string, occurredAt?: number): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.put(
      `/${twinId}/revoke-ownership`,
      {
        occurredAt,
      },
      this.requestConfig,
    );
  }

  public async verify(twinId: string, occurredAt?: number): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.post(
      `/${twinId}/verify`,
      {
        occurredAt,
      },
      this.requestConfig,
    );
  }

  public async setCustodian(
    twinId: string,
    custodian: string,
    occurredAt?: number,
  ): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.put(
      `/${twinId}/custodian`,
      {
        custodian,
        occurredAt,
      },
      this.requestConfig,
    );
  }

  public async setWarranty(
    twinId: string,
    warranty: Warranty | null,
    occurredAt?: number,
  ): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.put(
      `/${twinId}/warranty`,
      {
        warranty,
        occurredAt,
      },
      this.requestConfig,
    );
  }

  public async setNfcTag(twinId: string, nfcTag: NfcTag, occurredAt?: number): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.put(
      `/${twinId}/nfctag`,
      {
        nfcTag,
        occurredAt,
      },
      this.requestConfig,
    );
  }

  public async transfer(twinId: string, transfer: Transfer): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.post(`/${twinId}/transfer`, transfer, this.requestConfig);
  }

  public async cancelTransfer(twinId: string): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.post(`/${twinId}/transfer/cancel`, undefined, this.requestConfig);
  }

  public async acceptTransfer(twinId: string): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.post(`/${twinId}/transfer/accept`, undefined, this.requestConfig);
  }

  public async denyTransfer(twinId: string): Promise<AxiosResponse<DigitalTwin>> {
    return await this.httpClient.post(`/${twinId}/transfer/deny`, undefined, this.requestConfig);
  }

  public async createCertificate(input: CreateCertificate): Promise<AxiosResponse<Certificate>> {
    return await this.httpClient.post('/certificate', input, this.requestConfig);
  }

  public async updateCertificate(id: string, patch: UpdateCertificate): Promise<AxiosResponse<Certificate>> {
    return await this.httpClient.patch(`/certificate/${id}`, patch, this.requestConfig);
  }

  public async deleteCertificate(id: string): Promise<AxiosResponse<DeleteCertificateResponse>> {
    return await this.httpClient.delete(`/certificate/${id}`, this.requestConfig);
  }

  public async getAllCertificates(
    options: GetAllCertificatesOptions = {},
  ): Promise<AxiosResponse<CertificatesResponse>> {
    const { dateRange } = options;
    const url = '/certificate';
    return await this.httpClient.get(url, {
      params: {
        start: dateRange?.start,
        end: dateRange?.end,
      },
      ...this.requestConfig,
    });
  }

  public async createProvenanceMap(input: CreateProvenanceMap): Promise<AxiosResponse<ProvenanceMap>> {
    return await this.httpClient.post('/provenance-map', input, this.requestConfig);
  }

  public async updateProvenanceMap(id: string, patch: UpdateProvenanceMap): Promise<AxiosResponse<ProvenanceMap>> {
    return await this.httpClient.patch(`/provenance-map/${id}`, patch, this.requestConfig);
  }

  public async deleteProvenanceMap(id: string): Promise<AxiosResponse<DeleteProvenanceMapResponse>> {
    return await this.httpClient.delete(`/provenance-map/${id}`, this.requestConfig);
  }

  public async getAllProvenanceMaps(
    options: GetAllProvenanceMapsOptions = {},
  ): Promise<AxiosResponse<ProvenanceMapsResponse>> {
    const { dateRange } = options;
    const url = '/provenance-map';
    return await this.httpClient.get(url, {
      params: {
        start: dateRange?.start,
        end: dateRange?.end,
      },
      ...this.requestConfig,
    });
  }

  public async requestOwnership(
    input: Pick<OwnershipRequest, 'comment' | 'images' | 'twinId'>,
  ): Promise<AxiosResponse<OwnershipRequest>> {
    return await this.httpClient.post(`/ownership-request`, input, this.requestConfig);
  }

  public async respondToOwnershipRequest(
    requestId: string,
    input: AcceptOwnershipRequest | RejectOwnershipRequest | PendingOwnershipRequest,
  ): Promise<AxiosResponse<OwnershipRequest>> {
    return await this.httpClient.patch(`/ownership-request/${requestId}`, input, this.requestConfig);
  }
  public async getAllOwnershipRequests(
    options: GetAllOwnershipRequests = {},
  ): Promise<AxiosResponse<OwnershipRequestsResponse>> {
    const { continuationToken, limit, status } = options;
    const url = '/ownership-request';
    return await this.httpClient.get(url, {
      params: {
        continuationToken,
        limit,
        status,
      },
      ...this.requestConfig,
    });
  }
}
